import { useContext, useEffect } from 'react';

import { FilterOptions, ReactionCode, Sort } from '@vimeo/comments-ui';
import useSessionStorage from '@vimeo/core/hooks/use-storage/useClientStorage';
import { usePostVideoPrivateCommentReactions } from '@vimeo/gctl-api/hooks/useVideoPrivateCommentReactions';
import { usePostVideoPrivateCommentReplyReactions } from '@vimeo/gctl-api/hooks/useVideoPrivateCommentReplyReactions';
import { translate } from '@vimeo/i18n-runtime';

import { STASHED_REACTION_KEY } from '../../utils/constants';
import { getReviewPasswordHashFromCookie, idFromUri } from '../../utils/helper';
import { getStorageInstance } from '../../utils/inMemoryStorage';
import { guestLoginModalContext } from '../use-guestlogin-modal';
import { useAuthenticatedViewer, useViewer } from '../use-viewer';
import { usePrivateComments } from './use-get-comments';

const REACTION_FIELDS = ['reactionCode', 'uri', 'isByCurrentUser', 'userDisplayName'] as const;

interface PendingReaction {
  clipRequestId: string;
  commentId: string;
  reactionCode: ReactionCode;
  isReply?: boolean;
  parentCommentId?: string;
}

export function useAddCommentReaction(
  clipRequestId: string,
  isPublic: boolean,
  sort?: Sort,
  filterOptions?: FilterOptions,
  reviewId?: string,
) {
  const useHook = isPublic ? useAddPublicCommentReaction : useAddPrivateCommentReaction;
  return useHook(clipRequestId, sort, filterOptions, reviewId);
}

/**
 * Dummy hook for public comments to follow same pattern as other comment hooks and skip initializing get private comments
 * hook when public comments are being viewed — reactions are not supported on public (viewer) comments as of now.
 */
function useAddPublicCommentReaction(
  _clipRequestId: string,
  _sort?: Sort,
  _filterOptions?: FilterOptions,
  _reviewId?: string,
) {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const addReaction = async (_c: string, _r: ReactionCode, _i?: boolean, _p?: string) => {};
  return { addReaction };
}

/**
 * Hook to add emoji reactions to private (collaborator) comments and replies.
 * Performs an optimistic SWR cache update before the API call for instant UI feedback.
 */
function useAddPrivateCommentReaction(
  clipRequestId: string,
  sort?: Sort,
  filterOptions?: FilterOptions,
  reviewId?: string,
) {
  const [postCommentReaction] = usePostVideoPrivateCommentReactions();
  const [postReplyReaction] = usePostVideoPrivateCommentReplyReactions();
  const { revalidateComments } = usePrivateComments(
    clipRequestId,
    sort,
    filterOptions,
    undefined,
    reviewId,
  );
  const viewer = useViewer();
  const authenticatedViewer = useAuthenticatedViewer();
  const { toggleGuestLoginModal, guestUser, modalState } = useContext(guestLoginModalContext);
  const isLoggedIn = !!authenticatedViewer || !!guestUser;
  const password = getReviewPasswordHashFromCookie(reviewId);
  const storage = getStorageInstance();
  const {
    value: stashedReaction,
    set: setStashedReaction,
    remove: clearStashedReaction,
  } = useSessionStorage(STASHED_REACTION_KEY, null, undefined, storage);

  // If the guest login modal is closed without logging in, discard the stashed reaction so
  // it does not silently re-apply if the user later enters their name via the comment flow.
  useEffect(() => {
    if (!modalState.isActive && !isLoggedIn && stashedReaction) {
      clearStashedReaction();
    }
    // clearStashedReaction is excluded — it's not memoized in useSessionStorage and is
    // stable in behaviour; including it would cause this effect to run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.isActive, isLoggedIn, stashedReaction]);

  // After a page reload following guest user creation, execute the stashed reaction.
  // We check isLoggedIn (not just guestUser) because window.location.reload() fires before
  // setStashedGuestUser can run — after reload the viewer is authenticated via cookie, not
  // sessionStorage, so viewer?.user is set but guestUser remains null.
  useEffect(() => {
    if (!isLoggedIn) return;

    if (!stashedReaction) return;

    const pending: PendingReaction = JSON.parse(stashedReaction);
    if (pending.clipRequestId !== clipRequestId) return;

    clearStashedReaction();
    executeReaction(
      pending.commentId,
      pending.reactionCode,
      pending.isReply,
      pending.parentCommentId,
    );
    // executeReaction and clearStashedReaction are intentionally excluded — neither is
    // memoized in their respective hooks and including them would cause infinite re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, clipRequestId, stashedReaction]);

  const executeReaction = async (
    commentId: string,
    reactionCode: ReactionCode,
    isReply?: boolean,
    parentCommentId?: string,
  ) => {
    const optimisticUri = `optimistic-${Date.now()}-${reactionCode}`;
    const optimisticReaction = {
      reactionCode,
      uri: optimisticUri,
      isByCurrentUser: true,
      userDisplayName: viewer?.user?.name ?? '',
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getOptimisticAddData = (pages: any, action: 'add' | 'remove') => {
      if (!pages) return pages;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return pages.map((page: any) => ({
        ...page,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: page.data.map((comment: any) => {
          if (isReply && parentCommentId && comment.uri === parentCommentId) {
            return {
              ...comment,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              replies: comment.replies?.map((reply: any) =>
                reply.uri === commentId
                  ? {
                      ...reply,
                      reactions:
                        action === 'add'
                          ? [...(reply.reactions ?? []), optimisticReaction]
                          : (reply.reactions ?? []).filter(
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              (r: any) => r.uri !== optimisticUri,
                            ),
                    }
                  : reply,
              ),
            };
          }
          if (!isReply && comment.uri === commentId) {
            return {
              ...comment,
              reactions:
                action === 'add'
                  ? [...(comment.reactions ?? []), optimisticReaction]
                  : (comment.reactions ?? []).filter(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (r: any) => r.uri !== optimisticUri,
                    ),
            };
          }
          return comment;
        }),
      }));
    };

    revalidateComments(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (pages: any) => getOptimisticAddData(pages, 'add'),
      { revalidate: false },
    );

    try {
      if (isReply && parentCommentId) {
        await postReplyReaction({
          where: {
            videoId: clipRequestId as unknown as number,
            commentId: idFromUri(parentCommentId) as unknown as number,
            replyId: idFromUri(commentId) as unknown as number,
          },
          ...(reviewId || password ? { query: { reviewId, password } } : {}),
          select: REACTION_FIELDS,
          variables: { reactionCode },
        });
      } else {
        await postCommentReaction({
          where: {
            videoId: clipRequestId as unknown as number,
            commentId: idFromUri(commentId) as unknown as number,
          },
          ...(reviewId || password ? { query: { reviewId, password } } : {}),
          select: REACTION_FIELDS,
          variables: { reactionCode },
        });
      }
    } catch {
      // Revert optimistic update on failure
      revalidateComments(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (pages: any) => getOptimisticAddData(pages, 'remove'),
        { revalidate: false },
      );
    } finally {
      revalidateComments();
    }
  };

  const addReaction = async (
    commentId: string,
    reactionCode: ReactionCode,
    isReply?: boolean,
    parentCommentId?: string,
  ) => {
    if (!isLoggedIn) {
      // Stash the intended reaction so it can be re-applied after guest login
      const pending: PendingReaction = {
        clipRequestId,
        commentId,
        reactionCode,
        isReply,
        parentCommentId,
      };
      setStashedReaction(JSON.stringify(pending));
      toggleGuestLoginModal(true, {
        titleOverride: translate('Add your name to react'),
        submitButtonOverride: translate('React'),
      });
      return;
    }

    await executeReaction(commentId, reactionCode, isReply, parentCommentId);
  };

  return { addReaction };
}
